import React from 'react';
import { useAtom } from 'jotai';
import { integrationsAtom, syncEnabledAtom } from '../store';
import { Card } from '../components/ui/Card';
import { Database, Settings, Activity, AlertCircle, RotateCw } from 'lucide-react';
import toast from 'react-hot-toast';
import SyncJobsSettings from '../components/SyncJobsSettings';
import { useNavigate } from 'react-router-dom';
import ZoneDMCredentialsModal from '../components/ZoneDMCredentialsModal';
import FourDCredentialsModal from '../components/FourDCredentialsModal';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { syncInitialData } from '../services/sync';
import { initializeApi } from '../services/api';
import { initializeZoneDMApi } from '../services/zonedm';
import { resolveGhlAuth, toZoneDmCredentials, getOAuthTokenHealth } from '../services/ghlAuth';
import { getProxyEndpoint, getProxyHeaders } from '../services/proxy';

export default function IntegrationsManagement() {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useAtom(integrationsAtom);
  const [syncEnabled, setSyncEnabled] = useAtom(syncEnabledAtom);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = React.useState(false);
  const [isFourDModalOpen, setIsFourDModalOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const [facilities, setFacilities] = React.useState<any[]>([]);
  const [lastSyncTime4D, setLastSyncTime4D] = React.useState<string | null>(null);
  const [lastSyncTimeZoneDM, setLastSyncTimeZoneDM] = React.useState<string | null>(null);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [locationName, setLocationName] = React.useState<string | null>(null);

  // Check auth state
  React.useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const { data: zoneDMConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['zonedm-config'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('integrations')
        .select('*')
        .eq('type', 'zonedm')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    retry: 1,
    enabled: true,
  });

  // Fetch location details when OAuth is connected
  // Note: this runs after oauthStatus is declared below
  const fetchLocationDetails = React.useCallback(async () => {
    try {
      if (!zoneDMConfig?.user_id) return;
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`/api/ghl-location?user_id=${encodeURIComponent(zoneDMConfig.user_id)}`, {
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : {},
      });
      if (!resp.ok) return;
      const json = await resp.json();
      const name = json?.location?.name || json?.name || json?.location?.companyName || null;
      if (name) setLocationName(name);
    } catch {}
  }, [zoneDMConfig?.user_id]);

  const { data: fourDConfig, isLoading: isLoadingFourDConfig } = useQuery({
    queryKey: ['4d-config'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('integrations')
        .select('*')
        .eq('type', '4d')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    retry: 1,
    enabled: true,
  });

  // Effect to check if both integrations are active
  React.useEffect(() => {
    const canEnableSync = fourDConfig?.status === 'active' && zoneDMConfig?.status === 'active';
    setSyncEnabled(canEnableSync);
  }, [fourDConfig?.status, zoneDMConfig?.status, setSyncEnabled]);

  // Effect to update last sync times
  React.useEffect(() => {
    if (fourDConfig?.last_sync) {
      setLastSyncTime4D(new Date(fourDConfig.last_sync).toLocaleString());
    }
    if (zoneDMConfig?.last_sync) {
      setLastSyncTimeZoneDM(new Date(zoneDMConfig.last_sync).toLocaleString());
    }
  }, [fourDConfig?.last_sync, zoneDMConfig?.last_sync]);

  const testFourDConnection = useMutation({
    mutationFn: async (credentials: { 
      clientId: string; 
      clientSecret: string; 
      subscriptionKey?: string;
      endpointUrl?: string;
     }) => {
      const url = (credentials.endpointUrl || 'https://api.4d-emr.com/api/public') + '/facilitylocations';
      const response = await fetch(url, {
        headers: {
          'client-id': credentials.clientId,
          'client-secret': credentials.clientSecret,
          'subscription-key': credentials.subscriptionKey || 'd30017297ea649f08aaa1a33ef36986d',
        },
      });

      if (!response.ok) {
        throw new Error('Connection test failed');
      }

      const data = await response.json();
      return data;
    },
  });

  const saveFourDCredentials = useMutation({
    mutationFn: async (credentials: {
      clientId: string;
      clientSecret: string;
      subscriptionKey?: string;
      endpointUrl?: string;
      facilityId?: number;
      facilityName?: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Get existing integration
      const { data: existingIntegration } = await supabase
        .from('integrations')
        .select('id')
        .eq('type', '4d')
        .eq('user_id', user.id)
        .maybeSingle();

      // If we have a facilityId, this is the final save
      if (credentials.facilityId) {
        // Save with active status and facility info
        const finalIntegration = {
          id: existingIntegration?.id,
          user_id: user.id,
          type: '4d',
          config: credentials,
          status: 'active',
          facility_id: credentials.facilityId,
          facility_name: credentials.facilityName
        };

        // Initialize the API with credentials before syncing
        initializeApi({
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
          subscriptionKey: credentials.subscriptionKey || 'd30017297ea649f08aaa1a33ef36986d',
          endpointUrl: credentials.endpointUrl || 'https://api.4d-emr.com/api/public',
        });

        const { error: saveError } = await supabase
          .from('integrations')
          .upsert(finalIntegration, {
            onConflict: 'user_id,type',
          });

        if (saveError) throw saveError;

        // Initial data sync is triggered by the 4D modal with progress callbacks
      } else {
        // Save with inactive status for testing
        const testIntegration = {
          id: existingIntegration?.id,
          user_id: user.id,
          type: '4d',
          config: credentials,
          status: 'inactive'
        };

        const { error: updateError } = await supabase
          .from('integrations')
          .upsert(testIntegration, {
            onConflict: 'user_id,type',
          });

        if (updateError) throw updateError;
      }
      
      await queryClient.invalidateQueries({ queryKey: ['4d-config'] });
    },
  });

  const handleTestFourDConnection = async (credentials: {
    clientId: string;
    clientSecret: string;
    subscriptionKey?: string;
    endpointUrl?: string;
    facilityId?: number;
    facilityName?: string;
  }) => {
    try {
      // Initialize API instance first
      initializeApi({
        clientId: credentials.clientId,
        clientSecret: credentials.clientSecret,
        subscriptionKey: credentials.subscriptionKey || 'd30017297ea649f08aaa1a33ef36986d',
        endpointUrl: credentials.endpointUrl || 'https://api.4d-emr.com/api/public',
      });

      // If we have a facilityId, this is the final save after facility selection
      if (credentials.facilityId) {
        try {
          await saveFourDCredentials.mutateAsync(credentials);
          
          // If ZoneDM is connected, sync the new 4D credentials
          if (zoneDMConfig?.status === 'active') {
            const auth = resolveGhlAuth(zoneDMConfig);
            if (auth) {
              const zonedmApi = initializeZoneDMApi(toZoneDmCredentials(auth));

              await zonedmApi.sync4DCredentials({
                clientId: credentials.clientId,
                clientSecret: credentials.clientSecret,
                subscriptionKey: credentials.subscriptionKey || 'd30017297ea649f08aaa1a33ef36986d',
              });

              // Fetch HL calendars and upsert into Supabase
              try {
                const calendars = await zonedmApi.listCalendars();
                const list = Array.isArray(calendars?.data) ? calendars.data : (Array.isArray(calendars?.calendars) ? calendars.calendars : calendars);
                if (Array.isArray(list)) {
                  const rows = list.map((c: any) => ({
                    user_id: fourDConfig?.user_id || '',
                    hl_calendar_id: c?.id || c?.calendarId || c?.uid,
                    name: c?.name || c?.title || '',
                    group_name: c?.groupName || c?.group || '',
                  })).filter((r: any) => r.user_id && r.hl_calendar_id);
                  if (rows.length > 0) {
                    await supabase.from('hl_calendars').upsert(rows, { onConflict: 'user_id,hl_calendar_id' });
                  }
                }
              } catch (e) {
                console.warn('Failed to fetch HL calendars:', e);
              }
            }
          }
          
          toast.success('Connection saved and data sync started!');
          setIsFourDModalOpen(false);
          return;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to save credentials';
          console.error('Error saving credentials:', errorMessage);
          toast.error(errorMessage);
          throw new Error('Failed to save credentials and sync data');
        }
      }

      // Test the connection first
      const facilitiesData = await testFourDConnection.mutateAsync({
        clientId: credentials.clientId,
        clientSecret: credentials.clientSecret,
        subscriptionKey: credentials.subscriptionKey || 'd30017297ea649f08aaa1a33ef36986d',
        endpointUrl: credentials.endpointUrl || 'https://api.4d-emr.com/api/public',
      });

      if (!facilitiesData || !Array.isArray(facilitiesData) || facilitiesData.length === 0) {
        toast.error('No facilities found. Please check your credentials.');
        return;
      }

      setFacilities(facilitiesData);
      toast.success(
        facilitiesData.length === 1
          ? 'Connection successful! Confirm the facility below, then save.'
          : 'Connection successful! Please select a facility.'
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to 4D EMR';
      console.error('4D EMR Connection error:', errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleSyncNow = async () => {
    if (!fourDConfig?.config || !fourDConfig.facility_id || !fourDConfig.user_id) {
      toast.error('No facility configured');
      return;
    }

    // Prevent multiple syncs
    if (isSyncing) {
      return;
    }

    setIsSyncing(true);
    try {
      // Initialize API first
      initializeApi({
        clientId: fourDConfig.config.clientId,
        clientSecret: fourDConfig.config.clientSecret,
        subscriptionKey: fourDConfig.config.subscriptionKey,
      });

      // Sync FROM 4D (pull patients, appointments, quotes from 4D into Supabase)
      // Use fullHistoricalSync: true to get all data from 4D
      await syncInitialData(fourDConfig.user_id, undefined, undefined, { fullHistoricalSync: true });

      // Push appointments TO ZoneDM (last 7 days only)
      // This creates calendar events and opportunities in HighLevel
      try {
        console.log('Pushing appointments to ZoneDM (last 7 days)...');
        const pushUrl = `/.netlify/functions/push-appointments?user_id=${encodeURIComponent(fourDConfig.user_id)}&days=7&limit=50`;
        const { data: { session: pushSession } } = await supabase.auth.getSession();
        const pushRes = await fetch(pushUrl, {
          method: 'POST',
          headers: pushSession?.access_token
            ? { Authorization: `Bearer ${pushSession.access_token}` }
            : {},
        });
        if (!pushRes.ok) {
          console.warn('Push appointments failed:', await pushRes.text());
        } else {
          const pushData = await pushRes.json();
          console.log(`Pushed ${pushData.success || 0} appointments to ZoneDM`);
        }
      } catch (e) {
        console.error('Failed pushing appointments to ZoneDM:', e);
      }

      // Push Procedures into ZoneDM custom objects after 4D sync (manual sync as well)
      try {
        const { initializeZoneDMApi, syncProceduresCustomObjectsForUser } = await import('../services/zonedm');
        const { resolveGhlAuth, toZoneDmCredentials } = await import('../services/ghlAuth');
        const auth = resolveGhlAuth(zoneDMConfig);
        if (auth) {
          initializeZoneDMApi(toZoneDmCredentials(auth));
          await syncProceduresCustomObjectsForUser(fourDConfig.user_id);
        }
      } catch (e) {
        console.error('Failed pushing Procedures to ZoneDM custom objects:', e);
      }
      toast.success('Sync completed successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Sync failed:', errorMessage);
      toast.error(`Sync failed: ${errorMessage}`);
      // Clear any lingering toasts
      toast.dismiss('sync');
      toast.dismiss('sync-patients');
      toast.dismiss('sync-appointments');
    } finally {
      setIsSyncing(false);
    }
  };

  const testConnection = useMutation({
    mutationFn: async ({ pit, locationId }: { pit: string; locationId: string }) => {
      // Always test via our proxy to avoid CORS and domain mismatches
      const response = await fetch(getProxyEndpoint('higherlevel-proxy'), {
        method: 'POST',
        headers: { ...getProxyHeaders() },
        body: JSON.stringify({
          endpoint: `/locations/${locationId}/customFields`,
          method: 'GET',
          headers: { Authorization: `Bearer ${pit}`, Version: '2021-07-28' },
          baseUrl: 'https://services.leadconnectorhq.com',
        }),
      });

      if (!response.ok) {
        throw new Error('Connection test failed');
      }

      return response.json();
    },
  });

  const saveCredentials = useMutation({
    mutationFn: async (credentials: { pit: string; locationId: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // First try to get existing integration
      const { data: existing } = await supabase
        .from('integrations')
        .select('id')
        .eq('type', 'zonedm')
        .eq('user_id', user.id)
        .maybeSingle();

      const integration = {
        id: existing?.id, // Include id only if updating
        user_id: user.id,
        type: 'zonedm',
        config: credentials,
        status: 'active',
      };

      const { error } = await supabase.from('integrations').upsert(integration, {
        onConflict: 'user_id,type',
      });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      // Invalidate and refetch the zonedm-config query
      await queryClient.invalidateQueries({ queryKey: ['zonedm-config'] });
    },
    onError: (error) => {
      console.error('Error details:', error);
      console.error('Failed to save integration:', error);
      toast.error('Failed to save integration settings');
    },
  });

  // Helper: Determine connection method and OAuth status
  const hasOAuth = !!zoneDMConfig?.access_token;
  const oauthHealth = getOAuthTokenHealth(zoneDMConfig);
  const oauthStatus = hasOAuth
    ? 'connected'
    : zoneDMConfig?.status === 'error'
    ? 'error'
    : 'not_connected';
  const connectionMethod = hasOAuth ? 'oauth' : 'manual';

  React.useEffect(() => {
    if (oauthStatus === 'connected') {
      fetchLocationDetails();
    }
  }, [oauthStatus, fetchLocationDetails]);

  // Helper: Start OAuth flow
  const onStartOAuth = React.useCallback(async () => {
    // Get current user ID
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      toast.error('Please log in to connect OAuth');
      return;
    }

    // Use Vite environment variables for clientId and redirectUri
    const clientId = import.meta.env.VITE_GHL_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_GHL_REDIRECT_URI;
    const scopes = [
      'calendars.readonly',
      'calendars.write',
      'calendars/events.readonly',
      'calendars/events.write',
      'calendars/groups.readonly',
      'calendars/groups.write',
      'contacts.readonly',
      'contacts.write',
      'locations.readonly',
      'locations/customValues.readonly',
      'locations/customValues.write',
      'locations/customFields.readonly',
      'locations/customFields.write',
      'locations/tags.readonly',
      'locations/tags.write',
      'opportunities.readonly',
      'opportunities.write',
      'users.readonly',
      'custom-menu-link.readonly',
      'custom-menu-link.write',
      // Custom Objects & Associations
      'objects/schema.readonly',
      'objects/schema.write',
      'objects/record.readonly',
      'objects/record.write',
      'associations.readonly',
      'associations.write',
      'associations/relation.readonly',
      'associations/relation.write',
    ];
    
    // Pass user ID in state parameter
    const state = encodeURIComponent(JSON.stringify({ userId: session.user.id }));
    const authUrl = `https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scopes.join(' '))}&state=${state}`;
    window.location.href = authUrl;
  }, []);

  const handleTestConnection = async (credentials: { pit: string; locationId: string }) => {
    try {
      const customFields = await testConnection.mutateAsync(credentials);
      
      // Initialize ZoneDM API instance
      const zonedmApi = initializeZoneDMApi(credentials);
      
      // If 4D credentials exist, sync them to ZoneDM
      if (fourDConfig?.config) {
        await zonedmApi.sync4DCredentials({
          clientId: fourDConfig.config.clientId,
          clientSecret: fourDConfig.config.clientSecret,
          subscriptionKey: fourDConfig.config.subscriptionKey,
        });
      }
      
      await saveCredentials.mutateAsync(credentials);
      toast.success('Connection successful!');
      setIsCredentialsModalOpen(false);
    } catch (error) {
      console.error('ZoneDM connection error:', error);
      toast.error('Failed to connect to ZoneDM');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integration Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure and monitor your EMR and CRM integrations
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* 4D EMR Integration */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-indigo-50 rounded-lg">
                <Database className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">4D EMR Integration</h3>
                <p className="text-sm text-gray-500">Configure your 4D EMR connection</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                fourDConfig?.status && fourDConfig.status.toLowerCase() === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {fourDConfig?.status || 'Inactive'}
              </span>
              {fourDConfig?.config?.facilityName && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {fourDConfig.config.facilityName}
                </span>
              )}
              {fourDConfig?.facility_id && (
                <button
                  onClick={handleSyncNow}
                  disabled={isSyncing}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-50"
                  title="Sync Now"
                >
                  <RotateCw className={`w-5 h-5 ${isSyncing ? 'animate-spin' : ''}`} />
                </button>
              )}
              <button
                onClick={() => setIsFourDModalOpen(true)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
          {lastSyncTime4D ? (
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <Activity className="w-4 h-4 mr-2" />
              Last synced {lastSyncTime4D}
            </div>
          ) : (
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <Activity className="w-4 h-4 mr-2" />
              Not yet synced
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Subscription Key</label>
              <input
                type="password"
                value={fourDConfig?.config?.subscriptionKey && fourDConfig.config.subscriptionKey.trim() !== '' ? '••••••••••••••••' : ''}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Client ID</label>
              <input
                type="password"
                value={fourDConfig?.config?.clientId && fourDConfig.config.clientId.trim() !== '' ? '••••••••••••••••' : ''}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50"
              />
            </div>
          </div>
        </Card>

        {/* HighLevel CRM Integration */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-indigo-50 rounded-lg">
                <Database className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">ZoneDM Integration</h3>
                <p className="text-sm text-gray-500">Configure your ZoneDM connection</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                oauthHealth?.needsReconnect
                  ? 'bg-red-100 text-red-800'
                  : oauthHealth?.state === 'expired_refreshable' || oauthHealth?.state === 'expiring_soon'
                  ? 'bg-amber-100 text-amber-900'
                  : zoneDMConfig?.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {oauthHealth?.needsReconnect
                  ? 'reconnect'
                  : oauthHealth?.state === 'expired_refreshable'
                  ? 'token expired'
                  : oauthHealth?.state === 'expiring_soon'
                  ? 'expiring soon'
                  : zoneDMConfig?.status || 'Inactive'}
              </span>
              <button
                onClick={() => setIsCredentialsModalOpen(true)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
          {lastSyncTimeZoneDM ? (
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <Activity className="w-4 h-4 mr-2" />
              Last synced {lastSyncTimeZoneDM}
            </div>
          ) : (
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <Activity className="w-4 h-4 mr-2" />
              Not yet synced
            </div>
          )}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">OAuth Status</label>
            <div className="mt-1 flex items-center space-x-2">
              {oauthStatus === 'connected' && oauthHealth ? (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${oauthHealth.badgeClass}`}
                >
                  {oauthHealth.label}
                </span>
              ) : oauthStatus === 'connected' ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  Connected via OAuth
                </span>
              ) : (
                <span className="text-sm text-gray-600">Not Connected</span>
              )}
              <button
                className={`text-sm ${
                  oauthHealth?.needsReconnect
                    ? 'text-red-700 hover:text-red-600 font-medium'
                    : 'text-indigo-600 hover:text-indigo-500'
                }`}
                onClick={() => setIsCredentialsModalOpen(true)}
              >
                {oauthHealth?.needsReconnect
                  ? 'Reconnect required'
                  : oauthStatus === 'connected'
                  ? 'Reconnect'
                  : 'Connect OAuth'}
              </button>
            </div>

            {oauthStatus === 'connected' && oauthHealth && (
              <p
                className={`mt-2 text-sm flex items-start gap-1.5 ${
                  oauthHealth.needsReconnect || oauthHealth.state === 'expired_refreshable' || oauthHealth.state === 'expiring_soon'
                    ? 'text-amber-800'
                    : 'text-gray-600'
                }`}
              >
                {(oauthHealth.needsReconnect ||
                  oauthHealth.state === 'expired_refreshable' ||
                  oauthHealth.state === 'expiring_soon' ||
                  oauthHealth.state === 'missing_expiry') && (
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                )}
                <span>{oauthHealth.detail}</span>
              </p>
            )}

            {/* Location name display */}
            {oauthStatus === 'connected' && (
              <div className="mt-2 text-sm text-gray-600">
                {locationName ? (
                  <>Location: <span className="font-medium text-gray-800">{locationName}</span></>
                ) : (
                  'Fetching location details...'
                )}
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Manual PIT</label>
              <div className="mt-1 flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {zoneDMConfig?.config?.pit
                    ? 'Connected'
                    : hasOAuth
                    ? 'Not required (OAuth connected)'
                    : 'No PIT added'}
                </span>
                {!zoneDMConfig?.config?.pit && !hasOAuth && (
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Sync Settings */}
        <SyncJobsSettings />
      </div>

      <ZoneDMCredentialsModal
        isOpen={isCredentialsModalOpen}
        onClose={() => setIsCredentialsModalOpen(false)}
        onSave={handleTestConnection}
        isLoading={testConnection.isPending || saveCredentials.isPending}
        initialData={zoneDMConfig?.config}
        oauthStatus={oauthStatus}
        connectionMethod={connectionMethod}
        onStartOAuth={onStartOAuth}
      />
      
      <FourDCredentialsModal
        isOpen={isFourDModalOpen}
        onClose={() => {
          setIsFourDModalOpen(false);
          setFacilities([]);
        }}
        onSave={handleTestFourDConnection}
        isLoading={testFourDConnection.isPending || saveFourDCredentials.isPending}
        initialData={fourDConfig?.config}
        facilities={facilities}
      />
    </div>
  );
}
